import React from 'react';
import { Input } from '@/components/ui/atoms/Input/Input';
import { Button } from '@/components/ui/atoms/Button';
import { Check, X } from 'lucide-react';
import { isValidEmail } from '@/utils/candidate-email';

export interface EmailFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
  placeholder?: string;
  /** Compact layout for table inline use (reserved for callers; layout is atom-sized). */
  compact?: boolean;
  /** Hide save/cancel buttons (e.g. when used in dialog with external confirm) */
  showActions?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Molecule: email input with validation and optional save/cancel actions.
 * Single component for table, metacv (sidebar), and send dialog. Uses design tokens from index.css.
 */
export const EmailField: React.FC<EmailFieldProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  saving = false,
  placeholder = 'candidate@example.com',
  showActions = true,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const trimmed = value.trim();
  const valid = isValidEmail(trimmed);
  const canSave = valid && !saving && !disabled;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canSave) onSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div
      className='flex items-start  gap-1.5 flex-nowrap min-w-0'
      data-testid={dataTestId}
    >
      <Input
        type="email"
        rounded="full"
        size="sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled || saving}
        error={trimmed.length > 0 && !valid ? 'Enter a valid email' : undefined}
      />
      {showActions && (
        <span className="flex items-center gap-1.5 flex-shrink-0">
          <Button
            type="icon"
            intent="success"
            size="sm"
            onClick={onSave}
            disabled={!canSave}
            loading={saving}
            icon={<Check />}
            title="Save"
          />
          <Button
            type="icon"
            intent="secondary"
            size="sm"
            onClick={onCancel}
            disabled={saving}
            icon={<X />}
            title="Cancel"
          />
        </span>
      )}
    </div>
  );
};
